// RR Product Recs sagas called in product initialSaga & category initialSaga
import { call, put, select } from 'redux-saga/effects';

import { makeRequest } from './makeRequest';

import { isObj } from '../../../core/js/utils/reducerUtils';

import { saveRR, rrError } from '../../../core/js/actions/richRelevanceActions';

import { richRelevanceRoute, getGroundhogRecommendations } from '../utils/apiRoutes';

import { getProductsByArray } from '../../../product/js/sagas/productLookup';
import { applyFavorites } from '../../../product/js/utils/favoritesUtils';
import { useGroundhog } from '../utils/abTests';

import {
    orchestrateRecommendations,
    getRecommendationsSource,
    filterOutSoldOutProducts
} from '../utils/recommendationsUtils';

import { warn, error as logError } from '../../../core/js/utils/logger';

import { getSlugFromURL } from '../../../core/js/utils/urlUtils';

import { TILE_TYPE_CONTENTFUL_SLUGS } from '@core/js/constants';

import { setStrategyName } from '@core/js/utils/rrUtils';

/**
 * Makes a call to Rich Relevance to receive recommendations
 * @param  {String} placement Rich Relevance placement ID
 * @param  {Boolean} udd Rich Relevance's use dummy data (udd)
 * @return {object}           Results of the RR API call
 */
export function* getRRProductRecommendationsSaga(placement, udd = false) {
    console.log(`placement: ${JSON.stringify(placement)}`);

    const state = yield select();
    const rr = _.get(state, 'core.constants.thirdParty.default.rich_relevance', {});

    const rrApiPath = rr.path;
    const rrEndpoint = rr.endpoint;
    const rrClientKey = _.get(state,
        `core.constants.thirdParty.${state.core.siteId}.rich_relevance.client_key`, rr.client_key);
    const rrApiKey = _.get(state, `core.constants.thirdParty.${state.core.siteId}.rich_relevance.api_key`, rr.api_key);
    const rrUserId = state.core.profile.profileId || '';
    const rrSessionId = state.core.profile.tracerValue;

    const { styleNumber, categoryId } = yield call(getRecommendationsSource);

    const route = richRelevanceRoute(rrApiPath, rrEndpoint, rrClientKey, rrApiKey, rrUserId,
        rrSessionId, placement, styleNumber, categoryId, udd);

    return yield call(makeRequest, route);
}

/**
 * Get product recommendations from Groundhog
 * @return {object} Results from the Groundhog service
 */
export function* getGroundhogProductRecommendationsSaga() {
    const state = yield select();

    const { styleNumber, categoryId } = yield call(getRecommendationsSource);
    const route = getGroundhogRecommendations(state, styleNumber, 100);

    return yield call(makeRequest, route);
}

/**
 * Massages response from Rich Relevance or Groundhog into orchestrated recommendation products
 * @param  {object} productIds  One placement value from the recommendation placement array
 * @return {object}            The response from getRRProducts
 */
export function* getProductsFromIds(productIds) {
    const state = yield select();

    const products = yield call(getProductsByArray, state, productIds, '', 'productIds', true);

    return products.products;
}

export function* getProductsFromSlugs(placement) {
    const state = yield select();
    const contextPath = _.get(state, 'core.contextPath');

    function getProductSlug(product) {
        return getSlugFromURL(_.get(product, 'productURL', ''), contextPath);
    }

    const prodSlugArr = _.map(_.get(placement, 'recommendedProducts', []), getProductSlug);
    const products = yield call(getProductsByArray, state, prodSlugArr, '', 'productSlugs', true);

    return products.products;
}

export function* initializeGroundhogSaga(placementId, tileCount, tileTemplate = 'TITLE_ONLY') {
    try {
        const groundhogResponse = yield call(getGroundhogProductRecommendationsSaga);
        const rawGroundhogProducts = _.get(groundhogResponse, 'placements.0', []);
        const groundhogProductIds = _.map(_.get(rawGroundhogProducts, 'recommendedProducts', []), 'id');
        let products = yield call(getProductsFromIds, groundhogProductIds);

        products = orchestrateRecommendations(rawGroundhogProducts, products);
        const ghProducts = _.slice(filterOutSoldOutProducts(products), 0, tileCount);

        yield put(saveRR(ghProducts, placementId, rawGroundhogProducts.strategyName, tileTemplate));
    } catch (err) {
        if (isObj(err) && err.message) {
            yield put(rrError(err.message, location));
        } else {
            warn('Error initializing Groundhog sagas', JSON.stringify(err));
        }
    }
}

export function* processRRProducts(rawRRProducts, tileCount, tileTemplate = 'TITLE_ONLY') {
    let location;

    try {
        const placementType = _.get(rawRRProducts, 'placement');
        const state = yield select();

        location = placementType;

        let products = yield call(getProductsFromSlugs, rawRRProducts);

        products = orchestrateRecommendations(rawRRProducts, products);
        const rrProducts = _.slice(filterOutSoldOutProducts(products), 0, tileCount);

        applyFavorites(state, rrProducts);

        yield put(saveRR(rrProducts, rawRRProducts.placement, rawRRProducts.strategyName, tileTemplate));
    } catch (err) {
        if (isObj(err) && err.message) {
            yield put(rrError(err.message, location));
        } else {
            // unexpected error, log it
            logError('unexpected error processing RR products', err);
        }
    }
}

export function* initializeRRSaga(placementId, tileCount, tileTemplate = 'TITLE_ONLY') {
    try {
        const rrResponse = yield call(getRRProductRecommendationsSaga, placementId);

        for (const placement of _.get(rrResponse, 'placements', [])) {
            yield call(processRRProducts, placement, tileCount, tileTemplate);
        }
    } catch (err) {
        if (isObj(err) && err.message) {
            yield put(rrError(err.message, `initializeRRSaga: ${placementId}`));
        } else {
            warn('Error initializing Rich Relevance sagas', JSON.stringify(err));
        }
    }
}

export function* initializeDynamicYieldSaga(placementId, tileCount, tileTemplate = 'TITLE_ONLY') {
    const state = yield select();
    const placementKeys = placementId.split(',');

    if (!window.DYCS) {
        return;
    }

    const allPlacements = {};

    for (const placementKey of placementKeys) {
        try {
            const rawDYProducts = yield call(DYCS.recommendationWidgetData, placementKey);

            // Dynamic Yield can return products in two different formats.
            // PDP - a simple array of skus
            // Account - an object containing skus and a strategy name
            // The next 7 lines account for the differences

            let productIds = rawDYProducts;
            let strategy = placementKey;
            const feedId = _.get(rawDYProducts, 'feedId');
            const widgetId = _.get(rawDYProducts, 'widgetId');
            const showSeeMore = _.get(rawDYProducts, 'showSeeMore');
            let seeMoreUrl = _.get(rawDYProducts, 'seeMoreUrl');
            seeMoreUrl = showSeeMore && seeMoreUrl ? seeMoreUrl : null;

            if (_.get(rawDYProducts, 'skus') && (_.get(rawDYProducts, 'strategy'))) {
                productIds = rawDYProducts.skus;
                strategy = rawDYProducts.strategy;
            }

            let products = yield call(getProductsFromIds, productIds);

            products = orchestrateRecommendations(rawDYProducts, products);
            products = filterOutSoldOutProducts(products);
            products = applyFavorites(state, products);

            allPlacements[placementKey] = {
                products,
                strategy,
                tileTemplate,
                feedId,
                widgetId,
                seeMoreUrl
            };
        } catch (err) {
            if (isObj(err) && err.message) {
                yield put(rrError(err.message, placementKey));
            } else {
                warn('Error initializing Dynamic Yield Recommendation saga', JSON.stringify(err));
            }
        }
    }

    // if we have any placements, process them
    if (!_.isEmpty(Object.keys(allPlacements))) {
        yield call(processDYplacements, allPlacements, tileCount);
    }
}

export function* processDYplacements(allPlacements, tileCount) {
    const state = yield select();
    const primaryPlacementKey = _.get(
        state,
        'core.constants.thirdParty.default.dynamic_yield.placement_ids.productPrimary'
    );
    const productAlsoBoughtWithKey = _.get(
        state,
        'core.constants.thirdParty.default.dynamic_yield.placement_ids.productAlsoBoughtWith'
    );
    const visuallySimilarKey = _.get(
        state,
        'core.constants.thirdParty.default.dynamic_yield.placement_ids.productVisuallySimilar'
    );

    let primaryPlacementProducts = _.get(allPlacements, `${primaryPlacementKey}.products`, []);
    const secondaryPlacementProducts = _.get(allPlacements, `${productAlsoBoughtWithKey}.products`, []);

    for (const [placementKey, placementData] of Object.entries(allPlacements)) {
        let products = placementData.products || [];
        const feedId = placementData.feedId;
        const widgetId = placementData.widgetId;
        const seeMoreUrl = placementData.seeMoreUrl;

        // dedupe any products that already exist in the primaryPlacement
        if (!_.isEmpty(primaryPlacementProducts) && placementKey != primaryPlacementKey) {
            products = _.differenceBy(products, primaryPlacementProducts, 'product.productId');
        }

        if (!_.isEmpty(secondaryPlacementProducts) && placementKey == visuallySimilarKey) {
            products = _.differenceBy(products, secondaryPlacementProducts, 'product.productId');
        }

        // if we have any products, limit to tileCount and stick'em to the state
        if (!_.isEmpty(products)) {
            const {
                PDP_PRIMARY_RECOMMENDATIONS,
                PDP_SECONDARY_RECOMMENDATIONS,
                PDP_SOLD_OUT_RECOMMENDATIONS
            } = TILE_TYPE_CONTENTFUL_SLUGS;
            let tileTypeKey = '';
            products = _.slice(products, 0, tileCount);
            products = setStrategyName(products, `dyrectray-${placementData.strategy}`);

            if (placementKey === primaryPlacementKey) {
                primaryPlacementProducts = products;
                tileTypeKey = PDP_PRIMARY_RECOMMENDATIONS;
            }

            if (placementKey === productAlsoBoughtWithKey) {
                tileTypeKey = PDP_SECONDARY_RECOMMENDATIONS;
            }

            if (placementKey === visuallySimilarKey) {
                tileTypeKey = PDP_SOLD_OUT_RECOMMENDATIONS;
            }

            let tileTemplate = _.get(
                state,
                `core.featureConfigs.browseDisplayOptions.${tileTypeKey}`,
                placementData.tileTemplate
            );

            // TODO: UW-14895 - remove when SS_PDPTILETYPE test is over
            const tileTypeOverride = _.get(state, 'product.tileTypeOverride');
            tileTemplate = tileTypeOverride ? tileTypeOverride : tileTemplate;

            yield put(saveRR(
                products,
                placementKey,
                placementData.strategy,
                tileTemplate,
                true,
                feedId,
                widgetId,
                seeMoreUrl
            ));
        }
    }
}

export function* initializeRecommendationsSaga(placementId, tileCount, tileTemplate = 'TITLE_ONLY') {
    const state = yield select();
    if (useGroundhog(state)) {
        yield call(initializeGroundhogSaga, placementId, tileCount, tileTemplate);
    } else {
        yield call(initializeRRSaga, placementId, tileCount, tileTemplate);
    }
}

export default [];
